import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Drawer } from '@liveauctioneers/hammer-ui-core/drawer';
import { Gear } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { useCallback, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getUserExcludedHouses } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { fetchSearchAggregationsOnDemand } from '@/redux/modules/search/search.actions';
import {
    PreferencesDrawerClosedReason,
    useTrackPreferencesFiltersDrawerClosed,
    useTrackPreferencesFiltersDrawerOpened,
} from '@/utils/amplitude/analytics';
import { QuickFilterName, useTrackQuickFilterPillClicked } from '@/utils/amplitude/analytics/searchPage';
import { getPreferencesUrl } from '@/utils/urls';

import { EducationalTipExcludeHouses } from './components/EducationalTipExcludeHouses';
import { PreferencesFiltersExcludeHouses } from './components/PreferencesFiltersExcludeHouses';
import { PreferencesFiltersMain } from './components/PreferencesFiltersMain';

type Props = {
    setShowPreferencesDrawer: (show: boolean) => void;
    showPreferencesDrawer: boolean;
};

enum PreferencesFiltersOptions {
    ExcludeHouses = 'excludeHouses',
    Overview = 'overview',
}

type ExcludeHousesApplyState = {
    handleApply: () => Promise<void>;
    hasChanges: boolean;
};

const defaultExcludeHousesApplyState: ExcludeHousesApplyState = {
    handleApply: async () => {},
    hasChanges: false,
};

const EXCLUDE_HOUSES_DRAWER_BUTTON_ID = 'exclude-auction-houses-drawer-button';

export const PreferencesFilters = ({ setShowPreferencesDrawer, showPreferencesDrawer }: Props) => {
    const { formatMessage } = useIntl();
    const trackQuickFilterPillClicked = useTrackQuickFilterPillClicked();
    const dispatch = useAppDispatch();
    const [currentStep, setCurrentStep] = useState<PreferencesFiltersOptions>(PreferencesFiltersOptions.Overview);
    const [excludeHousesApplyState, setExcludeHousesApplyState] =
        useState<ExcludeHousesApplyState>(defaultExcludeHousesApplyState);

    const excludedHouseIds = useAppSelector((state) => getUserExcludedHouses(state));

    const trackPreferencesFiltersDrawerClosed = useTrackPreferencesFiltersDrawerClosed();
    const trackPreferencesFiltersDrawerOpened = useTrackPreferencesFiltersDrawerOpened();

    const onOpenPreferencesDrawer = useCallback(() => {
        trackPreferencesFiltersDrawerOpened();
        setShowPreferencesDrawer(true);
        setCurrentStep(PreferencesFiltersOptions.Overview);
    }, [setShowPreferencesDrawer, trackPreferencesFiltersDrawerOpened]);

    const handlePreferencesClose = useCallback(
        (reason: 'backdrop' | 'closeButton' | 'escape' | 'external') => {
            let reasonForClose: PreferencesDrawerClosedReason;
            switch (reason) {
                case 'backdrop':
                    reasonForClose = PreferencesDrawerClosedReason.Backdrop;
                    break;
                case 'closeButton':
                    reasonForClose = PreferencesDrawerClosedReason.CloseButton;
                    break;
                case 'escape':
                    reasonForClose = PreferencesDrawerClosedReason.Escape;
                    break;
                case 'external':
                    reasonForClose = PreferencesDrawerClosedReason.External;
                    break;
                default:
                    reasonForClose = PreferencesDrawerClosedReason.External;
            }
            trackPreferencesFiltersDrawerClosed({ reason: reasonForClose });
            setShowPreferencesDrawer(false);
            setCurrentStep(PreferencesFiltersOptions.Overview);
        },
        [setShowPreferencesDrawer, trackPreferencesFiltersDrawerClosed]
    );

    const handleBackToOverview = useCallback(() => {
        setCurrentStep(PreferencesFiltersOptions.Overview);
    }, []);

    const handleManageExclusionsClick = useCallback(() => {
        trackQuickFilterPillClicked(QuickFilterName.ExcludeAuctionHouses);
        dispatch(fetchSearchAggregationsOnDemand());
        setCurrentStep(PreferencesFiltersOptions.ExcludeHouses);
    }, [dispatch, trackQuickFilterPillClicked]);

    const handleApplyReady = useCallback((params: ExcludeHousesApplyState | null) => {
        setExcludeHousesApplyState(params ?? defaultExcludeHousesApplyState);
    }, []);

    const handleExcludeHousesApply = useCallback(async () => {
        await excludeHousesApplyState.handleApply();
        setShowPreferencesDrawer(false);
        setCurrentStep(PreferencesFiltersOptions.Overview);
        const successTitle = formatMessage({ id: 'excludeAuctionHousesFilter.modal.success.title' });
        toast(
            'success',
            {
                description: (
                    <BodyPrimary>
                        <FormattedMessage
                            id="excludeAuctionHousesFilter.modal.success.description"
                            values={{
                                accountSettingsLink: (
                                    <Link href={getPreferencesUrl()}>
                                        <FormattedMessage id="excludeAuctionHousesFilter.modal.success.accountSettingsLink" />
                                    </Link>
                                ),
                            }}
                        />
                    </BodyPrimary>
                ),
                title: successTitle,
            },
            {
                position: 'top-center',
            }
        );
    }, [excludeHousesApplyState, formatMessage, setShowPreferencesDrawer]);

    return (
        <>
            <EducationalTipExcludeHouses anchorSelect={EXCLUDE_HOUSES_DRAWER_BUTTON_ID} />
            <Button
                className="w-full !px-xs"
                iconLeft={Gear}
                id={EXCLUDE_HOUSES_DRAWER_BUTTON_ID}
                onClick={onOpenPreferencesDrawer}
                variant="outlined"
            >
                <FormattedMessage id="preferences" />
            </Button>
            <Drawer
                actions={
                    currentStep === PreferencesFiltersOptions.Overview ? (
                        <Button
                            onClick={() => handlePreferencesClose('closeButton')}
                            variant="transparent"
                        >
                            <FormattedMessage id="close" />
                        </Button>
                    ) : (
                        <div className="flex w-full gap-xs">
                            <Button
                                onClick={handleBackToOverview}
                                variant="outlined"
                            >
                                <FormattedMessage id="excludeAuctionHousesFilter.modal.cancelButton" />
                            </Button>
                            <Button
                                disabled={!excludeHousesApplyState.hasChanges}
                                onClick={handleExcludeHousesApply}
                            >
                                <FormattedMessage id="excludeAuctionHousesFilter.modal.applyButton" />
                            </Button>
                        </div>
                    )
                }
                anchor="left"
                className="z-[1000] w-full"
                handleClose={(reason) => handlePreferencesClose(reason)}
                hasShadow
                headerText={
                    currentStep === PreferencesFiltersOptions.Overview
                        ? formatMessage({ id: 'searchPage.mobileHeader.preferences' })
                        : formatMessage({ id: 'excludeAuctionHousesFilter.modal.title' })
                }
                keepInDOMWhileClosed={false}
                onBackButtonClick={
                    currentStep === PreferencesFiltersOptions.ExcludeHouses ? handleBackToOverview : undefined
                }
                show={showPreferencesDrawer}
                size="full"
            >
                {currentStep === PreferencesFiltersOptions.Overview ? (
                    <PreferencesFiltersMain
                        excludedHouseIdsCount={excludedHouseIds.length}
                        onManageExclusionsClick={handleManageExclusionsClick}
                    />
                ) : (
                    <PreferencesFiltersExcludeHouses onApplyReady={handleApplyReady} />
                )}
            </Drawer>
        </>
    );
};
